import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, MapPin, Ruler, DoorOpen, Calendar, User, Phone, Mail, Euro, Building2 } from 'lucide-react';
import { Property } from '../../../types/property';
import { Button } from '../../ui/Button';
import { cn } from '../../../lib/utils';

interface PropertyPreviewModalProps {
  property: Property;
  isOpen: boolean;
  onClose: () => void;
}

export const PropertyPreviewModal: React.FC<PropertyPreviewModalProps> = ({
  property,
  isOpen,
  onClose,
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'details' | 'photos' | 'contact'>('overview');

  // Gérer la fermeture avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Navigation photos avec flèches clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || activeTab !== 'photos') return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        setCurrentPhotoIndex(prev => 
          prev > 0 ? prev - 1 : (property.photos?.length || 1) - 1
        );
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        setCurrentPhotoIndex(prev => 
          prev < (property.photos?.length || 1) - 1 ? prev + 1 : 0
        );
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeTab, property.photos?.length]);

  if (!isOpen) return null;

  const photos = Array.isArray(property.photos) ? property.photos : [];
  const hasPhotos = photos.length > 0;

  const formatPrice = (price: number) => {
    return Math.floor(price || 0).toLocaleString('fr-FR').replace(/,/g, ' ') + ' DA';
  };

  const getStatusLabel = () => {
    const labels: Record<string, string> = {
      'for-sale': 'À vendre',
      'for-rent': 'À louer',
      'for-short-rent': 'Location courte durée',
      'estimation': 'Estimation',
      'sold': 'Vendu',
      'rented': 'Loué',
      'archived': 'Archivé',
    };
    return labels[property.status] || property.status;
  };

  const getStatusColor = () => {
    const colors: Record<string, string> = {
      'for-sale': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
      'for-rent': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
      'for-short-rent': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
      'estimation': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
      'sold': 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300',
      'rented': 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300',
      'archived': 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300',
    };
    return colors[property.status] || 'bg-gray-100 text-gray-800';
  };

  const getEquipments = () => {
    const equipments = [];
    if (property.has_elevator || property.features?.hasElevator) equipments.push('Ascenseur');
    if (property.has_parking || property.features?.hasParking) equipments.push('Parking');
    if (property.has_balcony || property.features?.hasBalcony) equipments.push('Balcon');
    if (property.has_terrace || property.features?.hasTerrace) equipments.push('Terrasse');
    if (property.has_air_conditioning || property.features?.hasAirConditioning) equipments.push('Climatisation');
    if (property.heating_type || property.features?.heatingType) {
      equipments.push(`Chauffage ${property.heating_type || property.features?.heatingType}`);
    }
    return equipments;
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex(prev => 
      prev < photos.length - 1 ? prev + 1 : 0
    );
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex(prev => 
      prev > 0 ? prev - 1 : photos.length - 1
    );
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Building2 },
    { id: 'details', label: 'Détails', icon: Ruler },
    { id: 'photos', label: `Photos (${photos.length})`, icon: Building2 },
    { id: 'contact', label: 'Contact', icon: User },
  ];

  // Gestionnaire pour empêcher la propagation des clics
  const handleModalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTabClick = (tabId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveTab(tabId as any);
  };

  const handlePhotoNavigation = (action: 'next' | 'prev' | 'select', index?: number) => {
    if (action === 'next') {
      nextPhoto();
    } else if (action === 'prev') {
      prevPhoto();
    } else if (action === 'select' && index !== undefined) {
      setCurrentPhotoIndex(index);
    }
  };
  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-0 sm:p-4"
      onClick={(e) => {
        // Fermer la modale si on clique sur l'arrière-plan
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="relative h-screen sm:h-[95vh] w-full max-w-7xl overflow-hidden rounded-none sm:rounded-xl bg-white shadow-2xl dark:bg-gray-900"
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-4 sm:p-6 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {property.type}
              </h2>
              <div className="flex items-center gap-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                <MapPin className="h-4 w-4" />
                <span>{property.address}</span>
              </div>
            </div>
            <span className={cn("rounded-full px-3 py-1 text-sm font-medium", getStatusColor())}>
              {getStatusLabel()}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-lg sm:text-2xl font-bold text-primary-500">
                {formatPrice(property.price)}
              </div>
              {property.net_price && property.net_price !== property.price && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Net vendeur: {formatPrice(property.net_price)}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="h-10 w-10"
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Navigation tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-2 sm:space-x-8 px-2 sm:px-4 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={(e) => handleTabClick(tab.id, e)}
                  className={cn(
                    'group inline-flex items-center gap-2 border-b-2 px-2 sm:px-1 py-3 text-xs sm:text-sm font-medium whitespace-nowrap',
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(95vh - 140px)' }}>
          {activeTab === 'overview' && (
            <div className="grid gap-8 lg:grid-cols-2">
              {/* Photo principale */}
              <div className="space-y-4">
                <div className="aspect-video overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                  {hasPhotos ? (
                    <img
                      src={photos[0]}
                      alt={property.type}
                      className="h-full w-full object-cover"
                      onClick={handleModalClick}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Building2 className="h-16 w-16 text-gray-400" />
                    </div>
                  )}
                </div>
                {hasPhotos && photos.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {photos.slice(1, 5).map((photo, index) => (
                      <img
                        key={index}
                        src={photo}
                        alt={`Photo ${index + 2}`}
                        className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
                        onClick={(e) => {
                          handleModalClick(e);
                          setCurrentPhotoIndex(index + 1);
                          setActiveTab('photos');
                        }}
                      />
                    ))}
                    {photos.length > 5 && (
                      <div 
                        className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-sm font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          handleModalClick(e);
                          setActiveTab('photos');
                        }}
                      >
                        +{photos.length - 5}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Informations principales */}
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Ruler className="h-4 w-4" />
                      Surface
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      {Math.floor(property.area || property.features?.area || 0)} m²
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <DoorOpen className="h-4 w-4" />
                      Pièces
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      {property.rooms || property.features?.rooms || 0}
                    </div>
                  </div>
                  {(property.floor !== undefined || property.features?.floor !== undefined) && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Étage</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        {property.floor ?? property.features?.floor ?? 'RDC'}
                      </div>
                    </div>
                  )}
                  {(property.construction_year || property.features?.constructionYear) && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="h-4 w-4" />
                        Année
                      </div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        {property.construction_year || property.features?.constructionYear}
                      </div>
                    </div>
                  )}
                </div>

                {/* Équipements */}
                {getEquipments().length > 0 && (
                  <div>
                    <h3 className="mb-3 text-lg font-medium text-gray-900 dark:text-white">
                      Équipements
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {getEquipments().map((equipment, index) => (
                        <span
                          key={index}
                          className="rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                        >
                          {equipment}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                {property.description && (
                  <div>
                    <h3 className="mb-3 text-lg font-medium text-gray-900 dark:text-white">
                      Description
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {property.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-8">
              {/* Caractéristiques détaillées */}
              <div>
                <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                  Caractéristiques détaillées
                </h3>
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Type de bien</span>
                      <span className="font-medium text-gray-900 dark:text-white">{property.type}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Surface</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {Math.floor(property.area || property.features?.area || 0)} m²
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Nombre de pièces</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {property.rooms || property.features?.rooms || 0}
                      </span>
                    </div>
                    {(property.bedrooms || property.features?.bedrooms) && (
                      <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                        <span className="text-gray-600 dark:text-gray-400">Chambres</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {property.bedrooms || property.features?.bedrooms}
                        </span>
                      </div>
                    )}
                    {(property.floor !== undefined || property.features?.floor !== undefined) && (
                      <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                        <span className="text-gray-600 dark:text-gray-400">Étage</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {property.floor ?? property.features?.floor ?? 'RDC'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    {(property.construction_year || property.features?.constructionYear) && (
                      <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                        <span className="text-gray-600 dark:text-gray-400">Année de construction</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {property.construction_year || property.features?.constructionYear}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Ascenseur</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {(property.has_elevator || property.features?.hasElevator) ? 'Oui' : 'Non'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Parking</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {(property.has_parking || property.features?.hasParking) ? 'Oui' : 'Non'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Balcon</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {(property.has_balcony || property.features?.hasBalcony) ? 'Oui' : 'Non'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Terrasse</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {(property.has_terrace || property.features?.hasTerrace) ? 'Oui' : 'Non'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Climatisation</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {(property.has_air_conditioning || property.features?.hasAirConditioning) ? 'Oui' : 'Non'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Informations financières */}
              <div>
                <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                  Informations financières
                </h3>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-lg bg-primary-50 p-4 dark:bg-primary-900/20">
                    <div className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400">
                      <Euro className="h-4 w-4" />
                      Prix de vente
                    </div>
                    <div className="text-xl font-bold text-primary-700 dark:text-primary-300">
                      {formatPrice(property.price)}
                    </div>
                  </div>
                  {property.net_price && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Prix net vendeur</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        {formatPrice(property.net_price)}
                      </div>
                    </div>
                  )}
                  {property.commission && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Commission</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        {formatPrice(property.commission)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="space-y-6">
              {hasPhotos ? (
                <>
                  {/* Photo principale en grand */}
                  <div className="relative">
                    <div className="aspect-video overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800" onClick={handleModalClick}>
                      <img
                        src={photos[currentPhotoIndex]}
                        alt={`Photo ${currentPhotoIndex + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    
                    {/* Navigation photos */}
                    {photos.length > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            handleModalClick(e);
                            handlePhotoNavigation('prev');
                          }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
                        >
                          <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                          onClick={(e) => {
                            handleModalClick(e);
                            handlePhotoNavigation('next');
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
                        >
                          <ChevronRight className="h-6 w-6" />
                        </button>
                        
                        {/* Indicateur de position */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm text-white">
                          {currentPhotoIndex + 1} / {photos.length}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Miniatures */}
                  {photos.length > 1 && (
                    <div className="grid grid-cols-6 gap-2">
                      {photos.map((photo, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            handleModalClick(e);
                            handlePhotoNavigation('select', index);
                          }}
                          className={cn(
                            "aspect-square overflow-hidden rounded-lg border-2",
                            currentPhotoIndex === index
                              ? "border-primary-500"
                              : "border-gray-200 hover:border-gray-300 dark:border-gray-700"
                          )}
                        >
                          <img
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex h-64 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                  <div className="text-center">
                    <Building2 className="mx-auto h-16 w-16 text-gray-400" />
                    <p className="mt-4 text-gray-500 dark:text-gray-400">
                      Aucune photo disponible
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-8">
              <div>
                <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                  Informations du propriétaire
                </h3>
                <div className="rounded-lg border border-gray-200 p-6 dark:border-gray-700">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {property.owner?.name || property.owner_name || 'Non spécifié'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">Propriétaire</div>
                      </div>
                    </div>
                    {(property.owner?.phone || property.owner_phone) && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {property.owner?.phone || property.owner_phone}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Téléphone</div>
                        </div>
                      </div>
                    )}
                    {(property.owner?.email || property.owner_email) && (
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {property.owner?.email || property.owner_email}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Email</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                  Agent en charge
                </h3>
                <div className="rounded-lg border border-gray-200 p-6 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {property.agent || property.created_by_name || 'Non assigné'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Agent immobilier</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer avec navigation */}
        <div className="border-t border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Référence: {property.id} • Créé le {new Date(property.createdAt || property.created_at).toLocaleDateString('fr-FR')}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="secondary" 
                onClick={(e) => {
                  handleModalClick(e);
                  onClose();
                }}
              >
                Fermer
              </Button>
              <Button 
                onClick={(e) => {
                  handleModalClick(e);
                  window.print();
                }}
              >
                Imprimer cette vue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};